import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Dumbbell, Flame, Loader2, AlertTriangle, Sparkles, ArrowRight, Clock, ListChecks } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SharedProgramPage = () => {
  const { token } = useParams();
  const [program, setProgram] = useState(null);
  const [kind, setKind] = useState('musculation');
  const [error, setError] = useState(false);

  useEffect(() => {
    fetch(`${BACKEND_URL}/api/tracking/programs-pro/shared/${token}`)
      .then((r) => (r.ok ? r.json() : Promise.reject()))
      .then((d) => { setProgram(d); setKind('musculation'); })
      .catch(() => {
        fetch(`${BACKEND_URL}/api/tracking/cardio-pro/shared/${token}`)
          .then((r) => (r.ok ? r.json() : Promise.reject()))
          .then((d) => { setProgram(d); setKind('cardio'); })
          .catch(() => setError(true));
      });
  }, [token]);

  if (error) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-6" data-testid="shared-program-error">
        <div className="text-center">
          <AlertTriangle className="w-10 h-10 text-zinc-600 mx-auto mb-4" />
          <p className="text-zinc-400 mb-4">Ce programme n'existe pas ou n'est plus disponible.</p>
          <Link to="/" className="text-primary font-semibold hover:underline">Retour à l'accueil</Link>
        </div>
      </div>
    );
  }

  if (!program) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center" data-testid="shared-program-loading">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  const Icon = kind === 'cardio' ? Flame : Dumbbell;

  return (
    <div className="min-h-screen bg-zinc-950" data-testid="shared-program-page">
      <div className="max-w-2xl mx-auto px-6 py-10">
        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[11px] font-bold bg-primary/20 text-primary border border-primary/30 mb-4">
          <Sparkles className="w-3 h-3" /> Programme partagé par un membre Body-Up
        </span>
        <h1 className="text-white text-2xl sm:text-4xl font-black leading-tight flex items-center gap-3 mb-2">
          <Icon className="w-7 h-7 text-primary shrink-0" /> {program.name}
        </h1>
        <p className="text-zinc-400 text-sm mb-8">{program.goal}</p>

        <div className="space-y-4" data-testid="shared-program-sessions">
          {program.sessions.map((s, i) => (
            <div key={i} className="rounded-2xl bg-zinc-900 border border-white/10 p-4">
              <div className="flex items-center justify-between mb-3">
                <h2 className="text-white font-bold text-base flex items-center gap-2">
                  <ListChecks className="w-4 h-4 text-primary" /> {s.session_label}
                </h2>
                <span className="text-xs text-zinc-500">{s.sessions_target} séances visées</span>
              </div>
              {kind === 'musculation' ? (
                <ul className="space-y-1.5">
                  {s.exercises.map((ex, j) => (
                    <li key={j} className="flex items-center justify-between text-sm text-zinc-300 border-b border-white/5 pb-1.5">
                      <span>{ex.name}</span>
                      <span className="text-zinc-500 text-xs">{ex.sets_count} x {ex.reps_scheme}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <ul className="space-y-1.5">
                  {(s.blocks || []).map((b, j) => (
                    <li key={j} className="flex items-center justify-between text-sm text-zinc-300 border-b border-white/5 pb-1.5">
                      <span>{b.exercises.map((e) => e.name).join(' + ')}</span>
                      <span className="text-zinc-500 text-xs inline-flex items-center gap-1"><Clock className="w-3 h-3" /> {b.rounds} x {b.work_seconds}s</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>

        <div className="mt-10 rounded-2xl bg-gradient-to-br from-primary/20 to-orange-500/10 border border-primary/30 p-6 text-center">
          <p className="text-white font-bold mb-1">Envie d'un programme sur-mesure comme celui-ci ?</p>
          <p className="text-sm text-zinc-400 mb-4">Body-Up construit votre programme personnalisé, calibré sur vos objectifs.</p>
          <Link to="/offres" className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-bold text-white bg-gradient-to-r from-primary to-orange-500 hover:scale-105 transition-transform" data-testid="shared-program-cta">
            Découvrir Body-Up <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </div>
  );
};

export default SharedProgramPage;
